import React, { useState, useMemo } from "react";
import { FiUser, FiUsers, FiCheck, FiRepeat } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { Modal } from "newui/components/ui/Modal";
import { StepperHeader } from "newui/components/Stepper/StepperHeader";
import type { PanelMode } from "../../types/panel.type";
import {
	EVALUATION_STEP_META,
	SOLO_WORKFLOW_STEPS,
	PANEL_WORKFLOW_STEPS,
} from "../../constants/evaluation.constants";
import { mapBackendStepToFilteredIndex } from "../RFxEvaluationSteps/RFxEvaluationSteps";

interface EvaluationModeSelectorProps {
	canChangeMode?: boolean;
	/** Post-creation props */
	currentMode?: PanelMode;
	currentStep?: number;
	hasAIAccess: boolean;
	isLoading?: boolean;
	onStart: (mode: PanelMode) => void;
	onModeChange?: (mode: PanelMode) => void;
}

interface ModeOption {
	description: string;
	icon: React.ReactNode;
	title: string;
	value: PanelMode;
}

const MODE_OPTIONS: ModeOption[] = [
	{
		value: "manual_solo",
		icon: <FiUser className="h-6 w-6" />,
		title: "Solo Evaluation",
		description: "Evaluate suppliers independently as the RFx Lead",
	},
	{
		value: "manual_panel",
		icon: <FiUsers className="h-6 w-6" />,
		title: "Panel Evaluation",
		description:
			"Invite team members to collaboratively evaluate suppliers",
	},
];

const isSoloMode = (mode: string): boolean =>
	mode === "manual_solo" || mode === "ai_solo";

const isPanelMode = (mode: string): boolean =>
	mode === "manual_panel" || mode === "ai_panel";

const isOptionActiveMode = (optionValue: PanelMode, activeMode: string): boolean =>
	(isSoloMode(activeMode) && isSoloMode(optionValue)) ||
	(isPanelMode(activeMode) && isPanelMode(optionValue));

const getWorkflowStepsForMode = (mode: PanelMode): readonly number[] => {
	if (mode === "manual_solo" || mode === "ai_solo") return SOLO_WORKFLOW_STEPS;
	return PANEL_WORKFLOW_STEPS;
};

const buildStepHeaderItems = (stepIds: readonly number[]) =>
	stepIds.reduce<{ description?: string; enabled: boolean; name: string; }[]>(
		(acc, id) => {
			const meta = EVALUATION_STEP_META.find((m) => m.id === id);
			if (meta) {
				acc.push({
					name: meta.name,
					description: meta.description,
					enabled: false,
				});
			}
			return acc;
		},
		[]
	);

export const EvaluationModeSelector: React.FC<EvaluationModeSelectorProps> = ({
	onStart,
	hasAIAccess,
	isLoading = false,
	currentMode,
	currentStep = 0,
	canChangeMode = false,
	onModeChange,
}) => {
	const [selectedMode, setSelectedMode] = useState<PanelMode | null>(null);
	const [confirmTarget, setConfirmTarget] = useState<PanelMode | null>(null);

	const hasStarted = currentMode != null;

	const handleStart = (): void => {
		if (selectedMode) {
			onStart(selectedMode);
		}
	};

	// Determine which workflow steps to show in the header
	const activeStepIds = useMemo(() => {
		if (hasStarted && currentMode) return getWorkflowStepsForMode(currentMode);
		if (selectedMode) return getWorkflowStepsForMode(selectedMode);
		return null;
	}, [hasStarted, currentMode, selectedMode]);

	const stepHeaderItems = useMemo(
		() => (activeStepIds ? buildStepHeaderItems(activeStepIds) : []),
		[activeStepIds]
	);

	const mappedCurrentStep = useMemo(() => {
		if (!hasStarted || !activeStepIds) return 0;
		return mapBackendStepToFilteredIndex(currentStep, activeStepIds);
	}, [hasStarted, currentStep, activeStepIds]);

	return (
		<div data-testid="evaluation-mode-selector" className="w-full">
			<h3 className="text-base font-medium text-gray-900 mb-4">
				{hasStarted ? "Evaluation Mode" : "Choose Evaluation Mode"}
			</h3>

			<div className="flex flex-row gap-4 mb-6">
				{MODE_OPTIONS.map((option) => {
					const isActive = hasStarted && isOptionActiveMode(option.value, currentMode);
					const isSelected = !hasStarted && selectedMode === option.value;
					const isHighlighted = isActive || isSelected;
					const showChangeButton =
						hasStarted && !isActive && canChangeMode && onModeChange != null;

					return (
						<div key={option.value} className="flex-1 flex flex-col">
							<button
								type="button"
								data-testid={`mode-card-${option.value}`}
								onClick={() => {
									if (!hasStarted) {
										setSelectedMode(option.value);
									}
								}}
								disabled={hasStarted}
								className={`
									relative flex flex-col items-center p-6 rounded-lg border-2
									transition-all duration-150 w-full text-center
									${isHighlighted
										? "border-blue-500 bg-blue-50"
										: `"border-gray-200`
									}
									${!isActive && "border-gray-300 bg-gray-100"}
									${!hasStarted ? "cursor-pointer hover:border-gray-300" : "cursor-default"}
								`}
							>
								{isHighlighted && (
									<div className="absolute top-2 right-2 text-blue-500">
										<FiCheck
											className="h-5 w-5"
											data-testid={`mode-check-${option.value}`}
										/>
									</div>
								)}
								{isActive && (
									<div className="absolute top-2 left-2">
										<span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
											Active
										</span>
									</div>
								)}
								<div
									className={`mb-3 ${isHighlighted ? "text-blue-500" : "text-gray-400"
										}`}
								>
									{option.icon}
								</div>
								<h4 className="text-sm font-semibold text-gray-900 mb-1">
									{option.title}
								</h4>
								<p className="text-xs text-gray-500">
									{option.description}
								</p>
								{hasAIAccess && (
									<span
										data-testid={`ai-badge-${option.value}`}
										className="mt-3 inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700"
									>
										AI-assisted
									</span>
								)}
							</button>

							{showChangeButton && (
								<button
									type="button"
									data-testid={`change-mode-${option.value}`}
									onClick={() => setConfirmTarget(option.value)}
									className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
								>
									<FiRepeat className="h-3.5 w-3.5" />
									Change
								</button>
							)}
						</div>
					);
				})}
			</div>

			{stepHeaderItems.length > 0 && (
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6" data-testid="evaluation-step-header">
					<h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Steps</h4>
					<StepperHeader
						steps={stepHeaderItems}
						currentStep={hasStarted ? mappedCurrentStep : 0}
						highestStep={hasStarted ? mappedCurrentStep : 0}
					/>
				</div>
			)}

			{!hasStarted && (
				<Button
					btntype="primary"
					onClick={handleStart}
					disabled={!selectedMode || isLoading}
					data-testid="start-evaluation-btn"
				>
					{isLoading ? "Starting..." : "Start Evaluation"}
				</Button>
			)}

			{confirmTarget && (
				<Modal
					isOpen={confirmTarget !== null}
					onClose={() => setConfirmTarget(null)}
					title="Change Evaluation Mode"
					modalType="alert"
					iconType="warning"
					primaryButton={{
						text: "Change Mode",
						onClick: () => {
							onModeChange!(confirmTarget);
							setConfirmTarget(null);
						},
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => setConfirmTarget(null),
					}}
				>
					<div className="flex gap-2 flex-col">
						Are you sure you want to change from {isSoloMode(currentMode!) ? "Solo Evaluation" : "Panel Evaluation"
						} to {isSoloMode(confirmTarget) ? "Solo Evaluation" : "Panel Evaluation"
						}?

						<div>This will return you back to the RFx Criteria selection page.</div>
					</div>
				</Modal>
			)}
		</div>
	);
};
